/**
 * Phira's opt-in "170" speed integrator, `useRpe170Speed: true` (W1-05a engine-eval; rpe-format.md F§7.3, R§2.3,
 * DESIGN.md §6.11). Reimplemented from the research description; no Phira code.
 *
 * Each speed layer is integrated on its own; the per-layer heights are summed right-associatively in f32.
 *
 *   events sorted by startTime beats (stable)
 *   kfs = [(0, 0, LINEAR)]; height = 0 (f64); cursor = 0; last = 0
 *   push_kf(a, b, tween, avg): b - a <= 1e-5 -> nothing; a keyframe at a (overwriting the last one when it is
 *       within 1e-5 of a) with value height and the tween; height += avg·(b - a)
 *   per event: a = max(sec(start), cursor), b = max(sec(end), a) (overlaps are compressed, not cut),
 *       s = start·R, e = end·R (f32);
 *       push_kf(cursor, a, LINEAR, last)                      gap: previous end speed; before the first event: 0
 *       if b > a + 1e-5:
 *         easingType 0  -> push_kf(a, b, LINEAR, s)           step: constant start speed
 *         easingType <= 1 -> linear ramp (split at the zero crossing x = s/(s - e) when s·e < 0), each piece
 *                           push_kf(.., speedLinearTween(s0, e0), (s0 + e0)/2)
 *         otherwise     -> speed_segment_tween(sub-mode, s, e, event tween)
 *       cursor = b; last = e
 *   push_kf(cursor, max_time, LINEAR, last); then (max_time, height, HOLD) unless the last keyframe is within 1e-5.
 *
 * speed_segment_tween, with f the event tween (static / clamped slice / bezier via the registry):
 *   Modern (META.RPEVersion >= 170): the speed follows the easing, v(x) = s + (e - s)·f(x);
 *     partial(x) = (e - s)·I(x) + s·x with I = IntStatic / IntClamped / GeneralInt (3-point Gauss-Legendre for
 *     bezier), avg = total = partial(1), height tween y(x) = partial(x)/total (0 for x <= 0, 1 for x >= 1, x when
 *     not finite);
 *   Legacy-170 (RPEVersion < 170): the HEIGHT follows the easing shape: d0 = f'(0), d1 = f'(1) (Phira's numeric
 *     derivative), k = (e - s)/(d1 - d0), b = s - k·d0, partial(x) = k·f(x) + b·x (so v = k·f'(x) + b runs from s to
 *     e); linear fallback when |d1 - d0| < 1e-8 or not finite.
 *   Both fall back to (linear, (s + e)/2) when total is not finite or |total| < 1e-5.
 *
 * Segments are stored as plain data (`EasedSegment`) in the track's typed-array tween table (curve.ts) and
 * dispatched by numeric kind; totals are computed through that same table code.
 *
 * Internal API: `buildRpe170Floor(eventLayers, env, maxTime, mode)`, `rpe170Mode(rpeVersionEffective)`,
 * `speedSegmentTween` and `integrateLayer170` (tests, readouts).
 */

import { createBezierCurve } from '../../ease/bezier';
import { derivative } from '../../ease/derivative';
import type { PhiraTweenDesc, Speed170Mode } from '../../ease/types';
import type { IrEvent, IrLayer } from '../../ir/types';
import { eventTweenDesc, tripleBeats, type EvalEnv } from '../anim';
import { layerList, SPEED_RATIO_F32 } from '../channels';
import {
  floorCurve,
  FloorTrack,
  FloorTweenBuilder,
  FloorTweenTable,
  zeroFloor,
  type EasedSegment,
  type FloorCurve,
} from './curve';

const fr = Math.fround;

/** Thresholds of the 170 integrator (f64 times, f32 totals). */
const TIME_EPS = 1e-5;
const TOTAL_EPS = fr(1e-5);
const DERIV_EPS = fr(1e-8);

/** Sub-mode from the effective RPEVersion: >= 170 Modern, otherwise Legacy-170. */
export function rpe170Mode(rpeVersionEffective: number): Speed170Mode {
  return rpeVersionEffective >= 170 ? 'modern' : 'legacy';
}

/** The tween of an eased segment: the segment itself, or null for the linear fallback; avg = its average speed. */
export interface SegmentTween {
  readonly eased: EasedSegment | null;
  readonly avg: number;
}

/**
 * partial(1) of a candidate segment, evaluated through the same table code the track uses (so the stored total
 * is exactly what the curve divides by).
 */
function probeTotal(seg: EasedSegment, probe: FloorTweenTable): number {
  probe.setEased(0, seg);
  return probe.partial(0, 1);
}

/** speed_segment_tween (eased 170 speed events). */
export function speedSegmentTween(
  mode: Speed170Mode,
  s: number,
  e: number,
  desc: PhiraTweenDesc,
  env: EvalEnv,
): SegmentTween {
  const fallback: SegmentTween = { eased: null, avg: fr(fr(s + e) / 2) };
  const curve =
    desc.kind === 'bezier' ? (env.bezier === null ? createBezierCurve(desc.p) : env.bezier.curve(desc.p)) : null;
  const probe = new FloorTweenTable(1);
  let a: number;
  let b: number;
  if (mode === 'modern') {
    a = fr(e - s);
    b = s;
  } else {
    // Legacy-170: the event tween's end slopes through Phira's numeric derivative.
    probe.setEased(0, { mode, desc, curve, a: 0, b: 0, total: 1 });
    const f = (x: number): number => probe.f.y(0, x);
    const d0 = derivative(f, 0);
    const d1 = derivative(f, 1);
    const dd = fr(d1 - d0);
    if (!Number.isFinite(dd) || !(Math.abs(dd) >= DERIV_EPS)) return fallback;
    a = fr(fr(e - s) / dd);
    b = fr(s - fr(a * d0));
    if (!Number.isFinite(a) || !Number.isFinite(b)) return fallback;
  }
  const total = probeTotal({ mode, desc, curve, a, b, total: 1 }, probe);
  if (!Number.isFinite(total) || Math.abs(total) < TOTAL_EPS) return fallback;
  return { eased: { mode, desc, curve, a, b, total }, avg: total };
}

class LayerIntegrator {
  readonly times: number[] = [0];
  readonly values: number[] = [0];
  readonly tweens = new FloorTweenBuilder().linear();
  height = 0;

  /**
   * push_kf's keyframe for [a, b): false when the interval is too short (nothing pushed). Otherwise the keyframe
   * at a exists (a last keyframe within 1e-5 of a is overwritten, its tween dropped) and the caller pushes
   * exactly one tween, then `advance(avg, b - a)`.
   */
  private open(a: number, b: number): boolean {
    if (b - a <= TIME_EPS) return false;
    const last = this.times.length - 1;
    if (Math.abs((this.times[last] ?? Number.NaN) - a) < TIME_EPS) {
      this.values[last] = fr(this.height);
      this.tweens.pop();
    } else {
      this.times.push(a);
      this.values.push(fr(this.height));
    }
    return true;
  }

  linear(a: number, b: number, avg: number): void {
    if (!this.open(a, b)) return;
    this.tweens.linear();
    this.height += avg * (b - a);
  }

  ramp(a: number, b: number, s: number, e: number, avg: number): void {
    if (!this.open(a, b)) return;
    this.tweens.ramp(s, e);
    this.height += avg * (b - a);
  }

  segment(a: number, b: number, seg: SegmentTween): void {
    if (!this.open(a, b)) return;
    if (seg.eased === null) this.tweens.linear();
    else this.tweens.easedSegment(seg.eased);
    this.height += seg.avg * (b - a);
  }

  finish(maxTime: number): FloorTrack {
    const last = this.times.length - 1;
    if (Math.abs((this.times[last] ?? Number.NaN) - maxTime) > TIME_EPS) {
      this.times.push(maxTime);
      this.values.push(fr(this.height));
      this.tweens.hold();
    }
    return new FloorTrack(this.times, this.values, this.tweens.build());
  }
}

/** One speed layer through the 170 integrator. */
export function integrateLayer170(
  events: readonly IrEvent<number>[],
  env: EvalEnv,
  maxTime: number,
  mode: Speed170Mode,
): FloorTrack {
  const sorted = events
    .map((e, i) => ({ e, i, beats: tripleBeats(e.startTime) }))
    .sort((x, y) => (x.beats < y.beats ? -1 : x.beats > y.beats ? 1 : x.i - y.i));
  const kf = new LayerIntegrator();
  let cursor = 0;
  let last = 0;
  for (const { e: ev } of sorted) {
    const a = Math.max(env.sec(ev.startTime), cursor);
    const b = Math.max(env.sec(ev.endTime), a);
    const s = fr(fr(ev.start) * SPEED_RATIO_F32);
    const e = fr(fr(ev.end) * SPEED_RATIO_F32);
    kf.linear(cursor, a, last);
    if (b > a + TIME_EPS) {
      if (ev.easingType === 0) {
        kf.linear(a, b, s);
      } else if (ev.easingType <= 1) {
        if (fr(s * e) < 0) {
          const x = fr(s / fr(s - e));
          const mid = a + (b - a) * x;
          kf.ramp(a, mid, s, 0, fr(s / 2));
          kf.ramp(mid, b, 0, e, fr(e / 2));
        } else {
          kf.ramp(a, b, s, e, fr(fr(s + e) / 2));
        }
      } else {
        kf.segment(a, b, speedSegmentTween(mode, s, e, eventTweenDesc(ev), env));
      }
    }
    cursor = b;
    last = e;
  }
  kf.linear(cursor, maxTime, last);
  return kf.finish(maxTime);
}

/** The 170 floor curve of a line (H = 0 when no layer has a speed list). */
export function buildRpe170Floor(
  eventLayers: readonly (IrLayer | null)[],
  env: EvalEnv,
  maxTime: number,
  mode: Speed170Mode,
): FloorCurve {
  const tracks: FloorTrack[] = [];
  for (const layer of eventLayers) {
    const list = layerList(layer, 'speed');
    if (list === undefined || list.length === 0) continue;
    tracks.push(integrateLayer170(list, env, maxTime, mode));
  }
  if (tracks.length === 0) return zeroFloor(maxTime);
  return floorCurve(mode === 'modern' ? 'rpe170-modern' : 'rpe170-legacy', maxTime, tracks);
}
